/**
 * SplitBadge Component
 *
 * A compact button/pill that indicates a transaction has split entries.
 * Shows the count of split allocations with expand/collapse functionality.
 *
 * @see SplitDetailsPanel for the expanded breakdown view
 */

import { PieChart, ChevronDown, ChevronUp } from 'lucide-react';
import clsx from 'clsx';

export interface SplitBadgeProps {
  /** Number of split entries (allocation count) */
  count: number;
  /** Whether the split details are currently expanded */
  isExpanded?: boolean;
  /** Callback when the badge is clicked to toggle expansion */
  onToggle?: () => void;
}

/**
 * SplitBadge renders a pill-shaped button showing the number of split entries.
 *
 * Features:
 * - Purple theme (bg-purple-100, text-purple-700)
 * - PieChart icon prefix
 * - Chevron icon indicating expand/collapse state
 * - Stops event propagation on click
 *
 * @example
 * ```tsx
 * <SplitBadge count={2} isExpanded={isOpen} onToggle={() => setIsOpen(!isOpen)} />
 * ```
 */
export function SplitBadge({ count, isExpanded, onToggle }: SplitBadgeProps) {
  const ChevronIcon = isExpanded ? ChevronUp : ChevronDown;

  return (
    <button
      data-testid="split-badge"
      onClick={(e) => {
        e.stopPropagation();
        onToggle?.();
      }}
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        'bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors',
        'focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-1'
      )}
    >
      <PieChart className="w-3 h-3" />
      <span>Split: {count}</span>
      <ChevronIcon className="w-3 h-3" />
    </button>
  );
}

export default SplitBadge;
